import React from "react";
import loadable from "@loadable/component";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Sentry } from "@compono/sentry-browser";

import { env } from "~/env";
import { useAuthorization } from "~/hooks/useAuthorization";

const ChatAiIslandHost = loadable(() => import("./ChatAiIslandHost"));

/**
 * Keeps a failing assistant from taking the application down with it.
 *
 * The mount sits at the top of `Private`, a sibling of `LayoutScreen`, and
 * every error boundary in platform-ui lives further down inside the sub-app
 * routers — so without this there is nothing above it to catch anything. A
 * chunk 404 after a deploy (stale index.html), a CDN blip, an ad-blocker
 * matching the chunk name, or the island throwing during module init would
 * unmount the React 17 root: a blank page on every route, for every user who
 * holds the flag and the permission.
 *
 * A `fallback` prop on the loadable would not help — @loadable/component
 * 5.16.7 rethrows a chunk-load error from render() before the fallback branch
 * is reached.
 *
 * Failing closed means no launcher. The assistant is an addition to the page,
 * so its absence costs the user nothing they came for.
 */
class ChatAiBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error);
  }

  render() {
    return this.state.failed ? null : <>{this.props.children}</>;
  }
}

export const ChatAiMount = () => {
  const { chatAiAssistant } = useFlags();
  const { permissions } = useAuthorization();

  // No endpoint means the chat-ai service was not deployed to this stage — PR
  // apps only get it with the `pr app - deploy chat-ai` label. Showing the
  // launcher there would offer a conversation that could never be answered,
  // so the widget stays away entirely rather than failing on first message.
  if (!env.CHAT_AI_URL) return null;
  if (!chatAiAssistant || !permissions.canAccessAiChat) return null;

  return (
    <ChatAiBoundary>
      <ChatAiIslandHost />
    </ChatAiBoundary>
  );
};
